import { isScalar, type RuntimeSchema, type ToolArg } from '@takeshape/schema';
import { pathToString } from '@takeshape/util';
import { getPreviewBranchRuntimeSchemaModel } from 'app/branches/selectors';
import type { ChangeFieldAction } from 'app/schema-form/actions';
import { initializeArray } from 'app/schema-form/actions';
import { fromJS } from 'immutable';
import type { SagaIterator } from 'redux-saga';
import { put, select } from 'redux-saga/effects';
import type { SetAgentsToolArgDefaultsCreator, SetAiToolArgDefaultsCreator } from './actions';

export function createToolArgsDefaultsSaga(
  formName: string,
  actionCreator: SetAiToolArgDefaultsCreator | SetAgentsToolArgDefaultsCreator,
  predicate: (action: ChangeFieldAction) => boolean
) {
  return function* toolArgsDefaultSaga(action: ChangeFieldAction): SagaIterator {
    if (typeof action.payload !== 'string' || action.meta.formName !== formName) {
      return;
    }

    if (!predicate(action)) {
      return;
    }

    const { payload, meta } = action;

    const { path } = meta;

    const runtimeSchema: RuntimeSchema | null = yield select(getPreviewBranchRuntimeSchemaModel);

    if (!runtimeSchema) {
      return;
    }

    const argsSchema = runtimeSchema.getQuery(payload as string)?.argsSchema;

    if (!argsSchema) {
      return;
    }

    const args: ToolArg[] = Object.entries(argsSchema.properties)
      .filter(([argName, arg]) => {
        return argsSchema.required?.includes(argName) || (typeof arg.type === 'string' && isScalar(arg.type));
      })
      .map(([argName]) => ({
        type: 'agent',
        argName
      }));

    const argsPath = [...path.slice(0, -1), 'args'];

    yield put(
      actionCreator(
        {
          args: fromJS(args)
        },
        {
          path: ['data', ...argsPath]
        }
      )
    );

    yield put(
      initializeArray(formName, pathToString(argsPath), {
        formName,
        path: argsPath,
        collapseByDefault: true,
        reinit: true
      })
    );
  };
}
